/**
 * apps/battle/client/src/terminal/components/KeyBar.tsx
 * Renders the bottom row of bracket-labelled keys.
 *
 * Each entry becomes a <button> so mobile tap == keyboard press. Hits the same
 * handler the useKeyboard hook fires.
 *
 * Visual: `[Enter] continue   [B] back to title`
 */
import type { ReactNode } from 'react';

export interface KeyBarItem {
  /** Display key like "Enter", "B", "Z1", "1-4", "Ctrl+Enter". */
  label: string;
  /** Description shown after the bracketed key. */
  description: string;
  /** Click/tap handler (mirrors the keyboard handler). */
  onActivate?: () => void;
  /** Optional: render bracket in portal-blue instead of accent-yellow. */
  variant?: 'default' | 'portal';
  /** If true, the button is rendered but disabled (no handler fires). */
  disabled?: boolean;
}

interface KeyBarProps {
  items: KeyBarItem[];
}

export function KeyBar({ items }: KeyBarProps) {
  return (
    <>
      {items.map((it, i) => (
        <KeyBarButton key={`${it.label}:${i}`} item={it} />
      ))}
    </>
  );
}

function KeyBarButton({ item }: { item: KeyBarItem }) {
  const bracketCls =
    item.variant === 'portal' ? 'tm-key__bracket tm-key__bracket--portal' : 'tm-key__bracket';

  const content: ReactNode = (
    <>
      <span className={bracketCls}>[{item.label}]</span> {item.description}
    </>
  );

  if (!item.onActivate || item.disabled) {
    return (
      <button type="button" className="tm-key" disabled aria-disabled="true">
        {content}
      </button>
    );
  }
  return (
    <button
      type="button"
      className="tm-key"
      onClick={(e) => { e.preventDefault(); item.onActivate!(); }}
    >
      {content}
    </button>
  );
}
